import { Theme, localizePhoneNumbers } from '@lucid/gatsby-theme-lucid';
import { Box, Heading } from 'lucid-components-react';
import React, { useContext } from 'react';
import ReactHtmlParser from 'react-html-parser';

import {
  Drupal_ParagraphChartHeaderSection,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphMarketoForm,
  Drupal_ParagraphOrCallCta,
  EnterpriseHeaderLogosJsonLogos
} from 'generated/graphql';
import Link from 'components/link/link';
import LucidContainer from 'components/lucid-container/lucid-container';
import LayoutContext from 'contexts/layoutContext';
import useMarketoLink from 'hooks/use-marketo-link';

interface ChartHeaderProps {
  data: Drupal_ParagraphChartHeaderSection;
  logos: (EnterpriseHeaderLogosJsonLogos | null)[];
}

const ChartHeader = ({ data, logos }: ChartHeaderProps) => {
  const {
    fieldTitle,
    fieldTitlePiece,
    fieldSubtitle,
    fieldCtaButtons,
    fieldDescription,
    fieldPhoneCta,
    fieldShowLogos
  } = data;
  const { language } = useContext(LayoutContext);
  const phoneCtaEntity = fieldPhoneCta
    ? (fieldPhoneCta.entity as Drupal_ParagraphOrCallCta)
    : null;

  const getMarketoLink = useMarketoLink(language);

  const fullTitle = fieldTitlePiece?.length
    ? fieldTitlePiece.filter(piece => piece?.trim()).join('<br>')
    : fieldTitle;

  return (
    <LucidContainer
      as="header"
      css={theme => ({
        paddingTop: theme.space[9],
        paddingBottom: theme.space[9],
        [`@media only screen and (max-width: ${theme.breakpoints.computer})`]: {
          paddingTop: theme.space[5],
          paddingBottom: theme.space[5]
        }
      })}
    >
      {fullTitle && (
        <>
          <Heading
            maxWidth={{ default: '100%', tabletSmall: '60rem' }}
            mb={{ default: 0, tabletSmall: 8 }}
            variant="headlineM"
          >
            {ReactHtmlParser(fullTitle)}
          </Heading>

          {fieldSubtitle && (
            <Heading
              as="h3"
              css={(theme: Theme) => ({
                maxWidth: '700px',
                margin: theme.space
                  ? `${theme.space[4]}px auto ${theme.space[5]}px`
                  : '20px auto 30px'
              })}
              variant="subheadS"
            >
              {fieldSubtitle}
            </Heading>
          )}

          {fieldDescription && <p>{fieldDescription}</p>}

          <Box
            className="chart-header__ctas"
            css={theme => ({
              alignItems: 'center',
              display: 'flex',
              'button,a': {
                margin: `${theme.space[0]}px ${theme.space[2]}px`,
                marginBottom: theme.space[2],
                '&:first-of-type': {
                  marginLeft: theme.space[0]
                }
              },
              [`@media only screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                marginTop: theme.space[5],
                flexDirection: 'column',
                'button,a': {
                  margin: `${theme.space[2]}px ${theme.space[0]}px`,
                  width: '100%',
                  textAlign: 'center'
                }
              }
            })}
          >
            {fieldCtaButtons!.map(cta => {
              const button = cta!.entity as Drupal_ParagraphCtaButton;
              const { url, copy } = getMarketoLink(
                (button?.fieldMarketo?.entity as Drupal_ParagraphMarketoForm)
                  ?.fieldMarketoId
              )!;

              return button.fieldButtonLink ? (
                <Link
                  key={button.fieldButtonText}
                  asButton
                  primary={!button.fieldIsSecondary}
                  secondary={!!button.fieldIsSecondary}
                  to={button.fieldButtonLink}
                >
                  {button.fieldButtonText}
                </Link>
              ) : (
                  /* eslint-disable-next-line react/jsx-indent */
                  <Link
                    key={button.fieldButtonText}
                    asButton
                    primary={!button.fieldIsSecondary}
                    secondary={!!button.fieldIsSecondary}
                    to={url}
                  >
                    {button.fieldButtonText || copy}
                  </Link>
                );
            })}

            {phoneCtaEntity && (
              <Box
                css={theme => ({
                  [`@media only screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                    width: '100%',
                    textAlign: 'center'
                  }
                })}
              >
                <Link
                  to={localizePhoneNumbers(
                    `tel:${phoneCtaEntity.fieldCallCtaPhoneNumber}`
                  )}
                >
                  {phoneCtaEntity.fieldCallCtaText}
                  &nbsp;
                  {localizePhoneNumbers(
                    phoneCtaEntity.fieldCallCtaPhoneNumber!
                  )}
                </Link>
              </Box>
            )}
          </Box>

          {fieldShowLogos && logos.length > 0 && (
            <Box
              css={theme => ({
                textAlign: 'center',
                width: '100%',
                maxWidth: '1018px',
                display: 'flex',
                flexFlow: 'row wrap',
                justifyContent: 'space-between',
                justifyItems: 'center',
                margin: `${theme.space[8]}px ${theme.space[0]}px ${theme.space[5]}px`,
                [`@media only screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                  padding: `${theme.space[0]}px ${theme.space[4]}px`,
                  margin: `${theme.space[4]}px ${theme.space[0]}px`
                },
                '+ .chart-header__ctas': {
                  margin: `${theme.space[4]}px auto`
                },
                '> div': {
                  flex: '0 1 auto',

                  [`@media only screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                    flex: '0 1 33.3333%',
                    marginBottom: theme.space[5],

                    '&:nth-of-type(n+4)': {
                      marginBottom: theme.space[0]
                    }
                  },

                  [`@media only screen and (max-width: ${theme.breakpoints.mobile})`]: {
                    flex: '0 1 45%',
                    marginBottom: theme.space[5],

                    '&:nth-of-type(n+5)': {
                      marginBottom: theme.space[0]
                    }
                  }
                }
              })}
            >
              {logos.map(logo => {
                return (
                  <Box key={logo!.alt}>
                    <img
                      alt={logo!.alt!}
                      css={theme => ({
                        flex: '0 1',
                        maxWidth: 140,
                        maxHeight: 40,
                        [`@media only screen and (max-width: ${theme.breakpoints.mobile})`]: {
                          maxWidth: '100%'
                        },
                        ...(logo!.url!.includes('.svg') && {
                          width: '100%',
                          maxWidth: '150px'
                        })
                      })}
                      src={logo!.url!}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </>
      )}
    </LucidContainer>
  );
};

export default ChartHeader;
